import { useState, useCallback, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  IdentificationIcon,
  CalculatorIcon,
  PhoneIcon,
  MapPinIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlusIcon,
  TrashIcon,
  AcademicCapIcon,
  PencilSquareIcon,
  ArrowUpTrayIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';

/* ─── Types ─── */
interface PracticeData {
  doctor_name_fr: string;
  doctor_name_ar: string;
  doctor_title_fr: string;
  doctor_title_ar: string;
  specialties_fr: string[];
  specialties_ar: string[];
  n_taxe: string;
  n_if: string;
  n_cnss: string;
  n_ice: string;
  n_inpe: string;
  phone_fix: string;
  phone_gsm: string;
  practice_email: string;
  website: string;
  address_line1: string;
  address_line2: string;
  city: string;
  postal_code: string;
  country: string;
  invoice_prefix: string;
  invoice_next_sequence: number;
  currency: string;
  default_payment_mode: string;
  show_signature_line: boolean;
  has_logo: boolean;
  has_stamp: boolean;
  logo_url: string | null;
  stamp_url: string | null;
  invoice_logo_size: number;
  invoice_stamp_width: number;
  invoice_stamp_height: number;
}

const EMPTY_DATA: PracticeData = {
  doctor_name_fr: '',
  doctor_name_ar: '',
  doctor_title_fr: '',
  doctor_title_ar: '',
  specialties_fr: [],
  specialties_ar: [],
  n_taxe: '',
  n_if: '',
  n_cnss: '',
  n_ice: '',
  n_inpe: '',
  phone_fix: '',
  phone_gsm: '',
  practice_email: '',
  website: '',
  address_line1: '',
  address_line2: '',
  city: '',
  postal_code: '',
  country: 'Morocco',
  invoice_prefix: 'F',
  invoice_next_sequence: 1,
  currency: 'Dh',
  default_payment_mode: 'Espèce',
  show_signature_line: true,
  has_logo: false,
  has_stamp: false,
  logo_url: null,
  stamp_url: null,
  invoice_logo_size: 75,
  invoice_stamp_width: 120,
  invoice_stamp_height: 60,
};

const PAYMENT_MODES = ['Espèce', 'Chèque', 'Virement', 'Carte'] as const;

export default function PracticeSettingsPage() {
  const { t } = useTranslation(['practice', 'common']);

  /* ─── State ─── */
  const [data, setData] = useState<PracticeData>(EMPTY_DATA);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  /* ─── Fetch on mount ─── */
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/practice/settings/', { credentials: 'include' });
        if (res.ok) {
          const json = await res.json();
          if (json.settings) setData({ ...EMPTY_DATA, ...json.settings });
        }
      } catch {
        setMsg({ type: 'error', text: t('actions.loadError') });
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  /* ─── Field updater ─── */
  const set = useCallback(<K extends keyof PracticeData>(field: K, value: PracticeData[K]) => {
    setData((prev) => ({ ...prev, [field]: value }));
  }, []);

  /* ─── Save ─── */
  const handleSave = useCallback(async () => {
    setSaving(true);
    setMsg(null);
    try {
      const { has_logo, has_stamp, logo_url, stamp_url, ...payload } = data;
      const res = await fetch('/api/practice/settings/update/', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setMsg({ type: 'success', text: t('actions.saveSuccess') });
      } else {
        const json = await res.json().catch(() => null);
        setMsg({ type: 'error', text: json?.error || t('actions.saveError') });
      }
    } catch {
      setMsg({ type: 'error', text: t('actions.saveError') });
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(null), 5000);
    }
  }, [data, t]);

  /* ─── Specialty list helpers ─── */
  const addSpecialty = (lang: 'specialties_fr' | 'specialties_ar') => {
    setData((prev) => ({ ...prev, [lang]: [...prev[lang], ''] }));
  };
  const removeSpecialty = (lang: 'specialties_fr' | 'specialties_ar', idx: number) => {
    setData((prev) => ({ ...prev, [lang]: prev[lang].filter((_, i) => i !== idx) }));
  };
  const updateSpecialty = (lang: 'specialties_fr' | 'specialties_ar', idx: number, val: string) => {
    setData((prev) => {
      const list = [...prev[lang]];
      list[idx] = val;
      return { ...prev, [lang]: list };
    });
  };

  /* ─── Image upload handlers ─── */
  const logoInputRef = useRef<HTMLInputElement>(null);
  const stampInputRef = useRef<HTMLInputElement>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingStamp, setUploadingStamp] = useState(false);

  const handleUploadImage = useCallback(async (type: 'logo' | 'stamp', file: File) => {
    const isLogo = type === 'logo';
    if (isLogo) setUploadingLogo(true); else setUploadingStamp(true);
    setMsg(null);
    try {
      const formData = new FormData();
      formData.append(type, file);
      const endpoint = isLogo ? '/api/practice/settings/upload-logo/' : '/api/practice/settings/upload-stamp/';
      const res = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const json = await res.json();
      if (res.ok && json.success) {
        setMsg({ type: 'success', text: isLogo ? 'Logo uploaded successfully' : 'Stamp uploaded successfully' });
        setData((prev) => ({
          ...prev,
          [isLogo ? 'has_logo' : 'has_stamp']: true,
          [isLogo ? 'logo_url' : 'stamp_url']: isLogo ? json.logo_url : json.stamp_url,
        }));
      } else {
        setMsg({ type: 'error', text: json.error || 'Upload failed' });
      }
    } catch {
      setMsg({ type: 'error', text: 'Upload failed' });
    } finally {
      if (isLogo) setUploadingLogo(false); else setUploadingStamp(false);
      setTimeout(() => setMsg(null), 5000);
    }
  }, []);

  const handleDeleteImage = useCallback(async (type: 'logo' | 'stamp') => {
    const isLogo = type === 'logo';
    setMsg(null);
    try {
      const endpoint = isLogo ? '/api/practice/settings/delete-logo/' : '/api/practice/settings/delete-stamp/';
      const res = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        setMsg({ type: 'success', text: isLogo ? 'Logo deleted' : 'Stamp deleted' });
        setData((prev) => ({
          ...prev,
          [isLogo ? 'has_logo' : 'has_stamp']: false,
          [isLogo ? 'logo_url' : 'stamp_url']: null,
        }));
      }
    } catch {
      setMsg({ type: 'error', text: 'Delete failed' });
    }
    setTimeout(() => setMsg(null), 5000);
  }, []);

  /* ─── Shared classes ─── */
  const inputCls =
    'w-full px-4 py-2.5 rounded-xl border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all duration-200';
  const labelCls = 'block text-sm font-medium text-foreground mb-1.5';
  const hintCls = 'text-xs text-muted-foreground mt-1';

  /* ─── Loading skeleton ─── */
  if (loading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6 animate-pulse">
        <div className="h-10 w-64 bg-muted rounded-xl" />
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="rounded-2xl border border-border bg-card p-6 space-y-4">
            <div className="h-6 w-48 bg-muted rounded" />
            <div className="grid grid-cols-2 gap-4">
              <div className="h-10 bg-muted rounded-xl" />
              <div className="h-10 bg-muted rounded-xl" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('title')} - Doctif</title>
      </Helmet>

      <div className="max-w-4xl mx-auto space-y-8">
        {/* ─── Header ─── */}
        <div className="flex items-center gap-4">
          <Link
            to="/settings"
            className="inline-flex items-center justify-center h-10 w-10 rounded-xl border border-border bg-card text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200"
          >
            <ArrowLeftIcon className="h-4.5 w-4.5" />
          </Link>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-foreground tracking-tight">{t('title')}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{t('subtitle')}</p>
          </div>
        </div>

        {/* ─── Global Alert ─── */}
        {msg && <Alert type={msg.type} text={msg.text} />}

        {/* ═══════════════════════════════════════════
            SECTION 1: DOCTOR & PRACTICE IDENTITY
        ═══════════════════════════════════════════ */}
        <Section
          icon={IdentificationIcon}
          iconColor="bg-blue-500/10 text-blue-500"
          title={t('sections.identity')}
          description={t('sections.identityDesc')}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>{t('identity.doctorNameFr')}</label>
              <input
                type="text"
                value={data.doctor_name_fr}
                onChange={(e) => set('doctor_name_fr', e.target.value)}
                placeholder={t('identity.doctorNameFrPlaceholder')}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>{t('identity.doctorNameAr')}</label>
              <input
                type="text"
                dir="rtl"
                value={data.doctor_name_ar}
                onChange={(e) => set('doctor_name_ar', e.target.value)}
                placeholder={t('identity.doctorNameArPlaceholder')}
                className={inputCls}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>{t('identity.doctorTitleFr')}</label>
              <input
                type="text"
                value={data.doctor_title_fr}
                onChange={(e) => set('doctor_title_fr', e.target.value)}
                placeholder={t('identity.doctorTitleFrPlaceholder')}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>{t('identity.doctorTitleAr')}</label>
              <input
                type="text"
                dir="rtl"
                value={data.doctor_title_ar}
                onChange={(e) => set('doctor_title_ar', e.target.value)}
                placeholder={t('identity.doctorTitleArPlaceholder')}
                className={inputCls}
              />
            </div>
          </div>
        </Section>

        {/* ═══════════════════════════════════════════
            SECTION 2: SPECIALTIES
        ═══════════════════════════════════════════ */}
        <Section
          icon={AcademicCapIcon}
          iconColor="bg-violet-500/10 text-violet-500"
          title={t('sections.specialties')}
          description={t('sections.specialtiesDesc')}
        >
          {/* French specialties */}
          <div>
            <label className={labelCls}>{t('specialties.specialtiesFr')}</label>
            <div className="space-y-2">
              {data.specialties_fr.map((s, idx) => (
                <div key={idx} className="flex gap-2">
                  <input
                    type="text"
                    value={s}
                    onChange={(e) => updateSpecialty('specialties_fr', idx, e.target.value)}
                    placeholder={t('specialties.specialtiesFrPlaceholder')}
                    className={inputCls}
                  />
                  <button
                    type="button"
                    onClick={() => removeSpecialty('specialties_fr', idx)}
                    className="flex-shrink-0 inline-flex items-center justify-center h-10 w-10 rounded-xl border border-destructive/30 text-destructive hover:bg-destructive/10 transition-colors"
                    title={t('specialties.removeSpecialty')}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addSpecialty('specialties_fr')}
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium transition-colors"
              >
                <PlusIcon className="h-4 w-4" />
                {t('specialties.addSpecialty')}
              </button>
            </div>
          </div>

          {/* Arabic specialties */}
          <div>
            <label className={labelCls}>{t('specialties.specialtiesAr')}</label>
            <div className="space-y-2">
              {data.specialties_ar.map((s, idx) => (
                <div key={idx} className="flex gap-2">
                  <input
                    type="text"
                    dir="rtl"
                    value={s}
                    onChange={(e) => updateSpecialty('specialties_ar', idx, e.target.value)}
                    placeholder={t('specialties.specialtiesArPlaceholder')}
                    className={inputCls}
                  />
                  <button
                    type="button"
                    onClick={() => removeSpecialty('specialties_ar', idx)}
                    className="flex-shrink-0 inline-flex items-center justify-center h-10 w-10 rounded-xl border border-destructive/30 text-destructive hover:bg-destructive/10 transition-colors"
                    title={t('specialties.removeSpecialty')}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addSpecialty('specialties_ar')}
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium transition-colors"
              >
                <PlusIcon className="h-4 w-4" />
                {t('specialties.addSpecialty')}
              </button>
            </div>
          </div>
        </Section>

        {/* ═══════════════════════════════════════════
            SECTION 3: FISCAL / REGISTRATION NUMBERS
        ═══════════════════════════════════════════ */}
        <Section
          icon={CalculatorIcon}
          iconColor="bg-amber-500/10 text-amber-500"
          title={t('sections.fiscal')}
          description={t('sections.fiscalDesc')}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>{t('fiscal.nTaxe')}</label>
              <input
                type="text"
                value={data.n_taxe}
                onChange={(e) => set('n_taxe', e.target.value)}
                placeholder={t('fiscal.nTaxePlaceholder')}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>{t('fiscal.nIf')}</label>
              <input
                type="text"
                value={data.n_if}
                onChange={(e) => set('n_if', e.target.value)}
                placeholder={t('fiscal.nIfPlaceholder')}
                className={inputCls}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div>
              <label className={labelCls}>{t('fiscal.nCnss')}</label>
              <input
                type="text"
                value={data.n_cnss}
                onChange={(e) => set('n_cnss', e.target.value)}
                placeholder={t('fiscal.nCnssPlaceholder')}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>{t('fiscal.nIce')}</label>
              <input
                type="text"
                value={data.n_ice}
                onChange={(e) => set('n_ice', e.target.value)}
                placeholder={t('fiscal.nIcePlaceholder')}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>{t('fiscal.nInpe')}</label>
              <input
                type="text"
                value={data.n_inpe}
                onChange={(e) => set('n_inpe', e.target.value)}
                placeholder={t('fiscal.nInpePlaceholder')}
                className={inputCls}
              />
            </div>
          </div>
        </Section>

        {/* ═══════════════════════════════════════════
            SECTION 4: CONTACT INFORMATION
        ═══════════════════════════════════════════ */}
        <Section
          icon={PhoneIcon}
          iconColor="bg-emerald-500/10 text-emerald-500"
          title={t('sections.contact')}
          description={t('sections.contactDesc')}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>{t('contact.phoneFix')}</label>
              <input
                type="tel"
                value={data.phone_fix}
                onChange={(e) => set('phone_fix', e.target.value)}
                placeholder={t('contact.phoneFixPlaceholder')}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>{t('contact.phoneGsm')}</label>
              <input
                type="tel"
                value={data.phone_gsm}
                onChange={(e) => set('phone_gsm', e.target.value)}
                placeholder={t('contact.phoneGsmPlaceholder')}
                className={inputCls}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>{t('contact.email')}</label>
              <input
                type="email"
                value={data.practice_email}
                onChange={(e) => set('practice_email', e.target.value)}
                placeholder={t('contact.emailPlaceholder')}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>{t('contact.website')}</label>
              <input
                type="url"
                value={data.website}
                onChange={(e) => set('website', e.target.value)}
                placeholder={t('contact.websitePlaceholder')}
                className={inputCls}
              />
            </div>
          </div>
        </Section>

        {/* ═══════════════════════════════════════════
            SECTION 5: ADDRESS
        ═══════════════════════════════════════════ */}
        <Section
          icon={MapPinIcon}
          iconColor="bg-rose-500/10 text-rose-500"
          title={t('sections.address')}
          description={t('sections.addressDesc')}
        >
          <div>
            <label className={labelCls}>{t('address.addressLine1')}</label>
            <input
              type="text"
              value={data.address_line1}
              onChange={(e) => set('address_line1', e.target.value)}
              placeholder={t('address.addressLine1Placeholder')}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>{t('address.addressLine2')}</label>
            <input
              type="text"
              value={data.address_line2}
              onChange={(e) => set('address_line2', e.target.value)}
              placeholder={t('address.addressLine2Placeholder')}
              className={inputCls}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div>
              <label className={labelCls}>{t('address.city')}</label>
              <input
                type="text"
                value={data.city}
                onChange={(e) => set('city', e.target.value)}
                placeholder={t('address.cityPlaceholder')}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>{t('address.postalCode')}</label>
              <input
                type="text"
                value={data.postal_code}
                onChange={(e) => set('postal_code', e.target.value)}
                placeholder={t('address.postalCodePlaceholder')}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>{t('address.country')}</label>
              <input
                type="text"
                value={data.country}
                onChange={(e) => set('country', e.target.value)}
                placeholder={t('address.countryPlaceholder')}
                className={inputCls}
              />
            </div>
          </div>
        </Section>

        {/* ═══════════════════════════════════════════
            SECTION 6: INVOICE PREFERENCES
        ═══════════════════════════════════════════ */}
        <Section
          icon={DocumentTextIcon}
          iconColor="bg-sky-500/10 text-sky-500"
          title={t('sections.invoice')}
          description={t('sections.invoiceDesc')}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>{t('invoice.prefix')}</label>
              <input
                type="text"
                value={data.invoice_prefix}
                onChange={(e) => set('invoice_prefix', e.target.value)}
                placeholder={t('invoice.prefixPlaceholder')}
                className={inputCls}
                maxLength={10}
              />
              <p className={hintCls}>{t('invoice.prefixHint')}</p>
            </div>
            <div>
              <label className={labelCls}>{t('invoice.nextSequence')}</label>
              <input
                type="number"
                min={1}
                value={data.invoice_next_sequence}
                onChange={(e) => set('invoice_next_sequence', Math.max(1, parseInt(e.target.value) || 1))}
                className={inputCls}
              />
              <p className={hintCls}>{t('invoice.nextSequenceHint')}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>{t('invoice.currency')}</label>
              <input
                type="text"
                value={data.currency}
                onChange={(e) => set('currency', e.target.value)}
                placeholder={t('invoice.currencyPlaceholder')}
                className={inputCls}
                maxLength={10}
              />
            </div>
            <div>
              <label className={labelCls}>{t('invoice.defaultPaymentMode')}</label>
              <select
                value={data.default_payment_mode}
                onChange={(e) => set('default_payment_mode', e.target.value)}
                className={inputCls}
              >
                {PAYMENT_MODES.map((mode) => (
                  <option key={mode} value={mode}>
                    {t(`invoice.paymentModes.${mode.toLowerCase().replace('è', 'e')}` as any, { defaultValue: mode })}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Section>

        {/* ═══════════════════════════════════════════
            SECTION 7: STAMP & SIGNATURE
        ═══════════════════════════════════════════ */}
        <Section
          icon={PencilSquareIcon}
          iconColor="bg-indigo-500/10 text-indigo-500"
          title={t('sections.stamp')}
          description={t('sections.stampDesc')}
        >
          {/* Signature toggle */}
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="text-sm font-medium text-foreground">{t('stamp.showSignatureLine')}</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={data.show_signature_line}
              onClick={() => set('show_signature_line', !data.show_signature_line)}
              className={`
                relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
                transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-2 focus:ring-offset-card
                ${data.show_signature_line ? 'bg-primary' : 'bg-muted'}
              `}
            >
              <span
                className={`
                  pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                  transition duration-200 ease-in-out
                  ${data.show_signature_line ? 'translate-x-5' : 'translate-x-0'}
                `}
              />
            </button>
          </div>

          {/* Logo upload */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* Logo */}
            <div className="space-y-3">
              <label className={labelCls}>
                <PhotoIcon className="h-4 w-4 inline mr-1.5 -mt-0.5" />
                Logo du cabinet
              </label>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleUploadImage('logo', file);
                  e.target.value = '';
                }}
              />
              {data.has_logo && data.logo_url ? (
                <div className="relative group">
                  <div className="w-full h-32 rounded-xl border border-border bg-muted/30 flex items-center justify-center overflow-hidden">
                    <img
                      src={data.logo_url}
                      alt="Logo"
                      className="max-h-28 max-w-full object-contain"
                    />
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    >
                      <ArrowUpTrayIcon className="h-4 w-4" />
                      {uploadingLogo ? 'Envoi...' : 'Changer'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteImage('logo')}
                      className="inline-flex items-center justify-center px-3 py-2 rounded-lg border border-destructive/30 text-destructive hover:bg-destructive/10 transition-colors"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={uploadingLogo}
                  className="w-full h-32 rounded-xl border-2 border-dashed border-border hover:border-primary/50 bg-muted/20 hover:bg-primary/5 flex flex-col items-center justify-center gap-2 transition-all duration-200 cursor-pointer group"
                >
                  {uploadingLogo ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
                  ) : (
                    <>
                      <ArrowUpTrayIcon className="h-6 w-6 text-muted-foreground group-hover:text-primary transition-colors" />
                      <span className="text-xs text-muted-foreground group-hover:text-primary transition-colors">
                        Télécharger le logo
                      </span>
                      <span className="text-[10px] text-muted-foreground/70">PNG, JPEG, SVG — max 5 Mo</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Stamp */}
            <div className="space-y-3">
              <label className={labelCls}>
                <PencilSquareIcon className="h-4 w-4 inline mr-1.5 -mt-0.5" />
                Cachet du cabinet
              </label>
              <input
                ref={stampInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleUploadImage('stamp', file);
                  e.target.value = '';
                }}
              />
              {data.has_stamp && data.stamp_url ? (
                <div className="relative group">
                  <div className="w-full h-32 rounded-xl border border-border bg-muted/30 flex items-center justify-center overflow-hidden">
                    <img
                      src={data.stamp_url}
                      alt="Cachet"
                      className="max-h-28 max-w-full object-contain"
                    />
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => stampInputRef.current?.click()}
                      disabled={uploadingStamp}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    >
                      <ArrowUpTrayIcon className="h-4 w-4" />
                      {uploadingStamp ? 'Envoi...' : 'Changer'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteImage('stamp')}
                      className="inline-flex items-center justify-center px-3 py-2 rounded-lg border border-destructive/30 text-destructive hover:bg-destructive/10 transition-colors"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => stampInputRef.current?.click()}
                  disabled={uploadingStamp}
                  className="w-full h-32 rounded-xl border-2 border-dashed border-border hover:border-primary/50 bg-muted/20 hover:bg-primary/5 flex flex-col items-center justify-center gap-2 transition-all duration-200 cursor-pointer group"
                >
                  {uploadingStamp ? (
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
                  ) : (
                    <>
                      <ArrowUpTrayIcon className="h-6 w-6 text-muted-foreground group-hover:text-primary transition-colors" />
                      <span className="text-xs text-muted-foreground group-hover:text-primary transition-colors">
                        Télécharger le cachet
                      </span>
                      <span className="text-[10px] text-muted-foreground/70">PNG, JPEG — max 5 Mo</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Invoice image size controls */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 pt-2">
            <div className="space-y-1.5">
              <label className={labelCls}>Taille logo (px)</label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={30}
                  max={200}
                  value={data.invoice_logo_size}
                  onChange={(e) => setData((d) => ({ ...d, invoice_logo_size: Number(e.target.value) }))}
                  className="flex-1 accent-primary"
                />
                <span className="text-sm text-muted-foreground w-10 text-right">{data.invoice_logo_size}</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className={labelCls}>Largeur cachet (px)</label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={30}
                  max={300}
                  value={data.invoice_stamp_width}
                  onChange={(e) => setData((d) => ({ ...d, invoice_stamp_width: Number(e.target.value) }))}
                  className="flex-1 accent-primary"
                />
                <span className="text-sm text-muted-foreground w-10 text-right">{data.invoice_stamp_width}</span>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className={labelCls}>Hauteur cachet (px)</label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={30}
                  max={200}
                  value={data.invoice_stamp_height}
                  onChange={(e) => setData((d) => ({ ...d, invoice_stamp_height: Number(e.target.value) }))}
                  className="flex-1 accent-primary"
                />
                <span className="text-sm text-muted-foreground w-10 text-right">{data.invoice_stamp_height}</span>
              </div>
            </div>
          </div>
        </Section>

        {/* ═══════════════════════════════════════════
            SAVE BUTTON
        ═══════════════════════════════════════════ */}
        <div className="flex justify-end pb-8">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-2 focus:ring-offset-card disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <CheckCircleIcon className="h-5 w-5" />
            {saving ? t('actions.saving') : t('actions.save')}
          </button>
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════
   Reusable sub-components
═══════════════════════════════════════════════════ */

function Section({
  icon: Icon,
  iconColor,
  title,
  description,
  children,
}: {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  iconColor: string;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-border bg-card overflow-hidden">
      <div className="p-6 border-b border-border">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${iconColor.split(' ')[0]}`}>
            <Icon className={`h-5 w-5 ${iconColor.split(' ')[1]}`} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">{title}</h2>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
        </div>
      </div>
      <div className="p-6 space-y-5">{children}</div>
    </section>
  );
}

function Alert({ type, text }: { type: 'success' | 'error'; text: string }) {
  return (
    <div
      className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium ${
        type === 'success'
          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800'
          : 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800'
      }`}
    >
      {type === 'success' ? (
        <CheckCircleIcon className="h-4 w-4 flex-shrink-0" />
      ) : (
        <XCircleIcon className="h-4 w-4 flex-shrink-0" />
      )}
      {text}
    </div>
  );
}
